import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import '../index.css';
import CustomTable, { Column } from './CustomTable';
import CustomerGroupModal from './CustomerGroupModal';
import moment from 'moment';
import apiClient from '../services/apiClient';
import EmptyState from './EmptyState';
import CustomerIcon from '../assets/svgs/CustomerIcon';
import ReactGA from 'react-ga4';
import { Button } from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';

interface CustomerGroup {
	id: number;
	name: string;
	description: string | null;
	customer_count: number;
	date_created: string;
	date_updated: string;
}

interface CustomerGroupsProps {}

export interface CustomerGroupsRef {
	refresh: () => void;
	showCreatePanel: () => void;
}

const CustomerGroups = forwardRef<CustomerGroupsRef, CustomerGroupsProps>((props, ref) => {
	const navigate = useNavigate();
	const [isEditPanelVisible, setIsEditPanelVisible] = useState<boolean>(false);
	const [selectedCustomerGroup, setSelectedCustomerGroup] = useState<CustomerGroup | null>(null);
	const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
	const [fetchState, setFetchState] = useState<'loading' | 'success' | 'failed'>('loading');
	const [isNewCustomerGroup, setIsNewCustomerGroup] = useState(false);

	useImperativeHandle(ref, () => ({
		refresh: fetchCustomerGroups,
		showCreatePanel: showCreateCustomerGroupPanel,
	}));

	// Columns definition for CustomTable
	const columns: Column[] = [
		{
			field: 'name',
			headerName: 'Group Name',
			minWidth: 200,
			headerClassName: 'table-header',
			cellClassName: 'font-medium text-gray-900',
			flex: 2,
		},
		{
			field: 'description',
			headerName: 'Description',
			minWidth: 300,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => params.value || '-',
			flex: 3,
		},
		{
			field: 'customer_count',
			headerName: 'Customers',
			minWidth: 100,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => `${params.value || 0} customer${params.value !== 1 ? 's' : ''}`,
			flex: 1,
		},
		{
			field: 'date_created',
			headerName: 'Created',
			minWidth: 150,
			headerClassName: 'table-header',
			cellClassName: 'text-gray-500',
			renderCell: (params) => moment(params.value).format('Do MMM YYYY'),
			flex: 1,
		},
		{
			field: 'actions',
			headerName: 'Actions',
			minWidth: 120,
			headerClassName: 'table-header',
			flex: 1,
			renderCell: (params) => (
				<Button
					size="small"
					variant="outlined"
					startIcon={<AssessmentIcon />}
					onClick={(e) => {
						e.stopPropagation(); // Prevent row click
						navigate(`/customers/groups/${params.row.id}/reports`);
						ReactGA.event({
							category: 'Customer Groups',
							action: 'Clicked Reports Button',
							label: `Group ID: ${params.row.id}`,
						});
					}}
					sx={{ fontSize: '0.75rem', padding: '4px 8px' }}
				>
					Reports
				</Button>
			),
		},
	];

	useEffect(() => {
		fetchCustomerGroups();
	}, []);

	const fetchCustomerGroups = () => {
		setFetchState('loading');
		apiClient
			.get('/customer-groups')
			.then((response: any) => {
				setFetchState('success');
				const customerGroupsData = Array.isArray(response.data)
					? response.data
					: response.data?.data || response || [];
				setCustomerGroups(customerGroupsData);
				ReactGA.event({
					category: 'Customer Groups',
					action: 'Fetched Customer Groups',
					label: 'Fetch Success',
				});
			})
			.catch((error) => {
				console.error('Error fetching customer groups:', error);
				setFetchState('failed');
				setCustomerGroups([]); // Set empty array on error
				ReactGA.event({
					category: 'Customer Groups',
					action: 'Fetch Customer Groups Failed',
					label: 'Fetch Failed',
				});
			});
	};

	const handleRowClick = (params: { row: CustomerGroup }) => {
		const customerGroup = params.row;
		setSelectedCustomerGroup(customerGroup);
		setIsNewCustomerGroup(false);
		setIsEditPanelVisible(true);

		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Customer Group Row',
			label: `Customer Group ID: ${customerGroup.id}`,
		});
	};

	const showCreateCustomerGroupPanel = () => {
		ReactGA.event({
			category: 'User Interaction',
			action: 'Clicked Add New Customer Group Button',
			label: 'Add New Customer Group',
		});
		setSelectedCustomerGroup(null);
		setIsNewCustomerGroup(true);
		setIsEditPanelVisible(true);
	};

	const handleCustomerGroupUpdate = (updatedCustomerGroup: any) => {
		// Update the customer group in the list
		if (updatedCustomerGroup.id) {
			setCustomerGroups(
				customerGroups.map((cg) => (cg.id === updatedCustomerGroup.id ? updatedCustomerGroup : cg))
			);
		}
	};

	const handleCustomerGroupDelete = (customerGroupId: number) => {
		// Remove the customer group from the list
		setCustomerGroups(customerGroups.filter((cg) => cg.id !== customerGroupId));
		setIsEditPanelVisible(false);
		ReactGA.event({
			category: 'Customer Groups',
			action: 'Deleted Customer Group',
			label: `Customer Group ID: ${customerGroupId}`,
		});
	};

	const hideCreateCustomerGroupPanel = (customerGroup: CustomerGroup | null = null) => {
		const wasCreatingNew = isNewCustomerGroup;
		setIsEditPanelVisible(false);
		setSelectedCustomerGroup(null);
		setIsNewCustomerGroup(false);

		if (customerGroup) {
			if (wasCreatingNew) {
				// This is a new customer group - add it to the list
				setCustomerGroups([...customerGroups, customerGroup]);
				ReactGA.event({
					category: 'Customer Groups',
					action: 'Created New Customer Group',
					label: 'New Customer Group',
				});
			} else {
				// This is an update to existing customer group
				setCustomerGroups(
					customerGroups.map((cg: CustomerGroup) => {
						if (cg.id === customerGroup.id) {
							return customerGroup;
						}
						return cg;
					})
				);
				ReactGA.event({
					category: 'Customer Groups',
					action: 'Updated Customer Group',
					label: `Customer Group ID: ${customerGroup.id}`,
				});
			}
		} else {
			ReactGA.event({
				category: 'User Interaction',
				action: 'Closed Customer Group Panel',
				label: 'No Customer Group',
			});
		}
	};

	return (
		<>
			<CustomTable
				columns={columns}
				objects={customerGroups || []}
				sx={{ marginTop: '50px' }}
				onRowClick={handleRowClick}
				fetchState={fetchState}
				initialRowsPerPage={25}
				emptyStateComponent={() => (
					<EmptyState
						icon={<CustomerIcon />}
						title="No customer groups yet"
						description="Create your first customer group to organize customers"
					/>
				)}
			/>
			<CustomerGroupModal
				isVisible={isEditPanelVisible}
				handleHidePanel={hideCreateCustomerGroupPanel}
				customerGroup={selectedCustomerGroup}
				isNewCustomerGroup={isNewCustomerGroup}
				onCustomerGroupUpdate={handleCustomerGroupUpdate}
				onCustomerGroupDelete={handleCustomerGroupDelete}
			/>
		</>
	);
});

CustomerGroups.displayName = 'CustomerGroups';

export default CustomerGroups;
